import { type ShippingOrderListModel, type ShippingOrderParticipantListModel, ShippingOrderTitleStatus } from "api";
import BookIndex from "components/shared/book/list";
import { Transition } from "components/shared/column";
import SharedReactTable from "components/shared/reactTable";
import { ShippingOrderDeclineBadge, ShippingOrderStatusBadge } from "components/shared/shippingOrderInfo";
import { dateToView } from "helpers/date";
import { paths } from "helpers/paths";
import { DateRangeColumnFilter, rowIndexColumn } from "helpers/reactTable";
import { tableContainer } from "helpers/redux";
import React, { FC, MouseEvent } from "react";
import { Link } from "react-router-dom";
import { getShippingOrderRegistryStatusLabel, ShippingOrderCreatedFilter, ShippingOrderStatusFilter } from "./status";

type ParticipantKey = "shipper" | "carrier";

const participantColumn = (Header: string, participantKey: ParticipantKey) => ({
    Header,
    id: `${participantKey}/fullName`,
    accessor: (item: ShippingOrderListModel) => item[participantKey]?.fullName || "",
    filterable: true,
    sortable: true,
    Cell: ({ row: { original } }: { row: { original: ShippingOrderListModel } }) => {
        const participant = original[participantKey] as ShippingOrderParticipantListModel | null | undefined;
        const requisites = [participant?.inn && `ИНН ${participant.inn}`, participant?.kpp && `КПП ${participant.kpp}`]
            .filter(Boolean)
            .join(", ");
        return (
            <div>
                <div>{participant?.fullName || "—"}</div>
                {requisites && <div className="small text-muted">{requisites}</div>}
            </div>
        );
    },
});

class ShippingOrdersList extends BookIndex {
    state: any;
    props: any;

    get tableColumns() {
        return [
            rowIndexColumn,
            {
                Header: "Номер ЭЗЗ",
                accessor: "orderNumber",
                filterable: true,
                sortable: true,
                Cell: ({ value, row: { original } }: { value: string; row: { original: ShippingOrderListModel } }) => (
                    <Link
                        to={paths.shippingOrdersShow(original.id)}
                        onClick={(event: MouseEvent<HTMLAnchorElement>) => event.stopPropagation()}
                    >
                        {value || `ЭЗЗ №${original.id}`}
                    </Link>
                ),
            },
            {
                Header: "Дата",
                accessor: "orderDate",
                filterable: true,
                filterType: "dateRange",
                sortable: true,
                Filter: DateRangeColumnFilter,
                Cell: ({ value }: { value?: string | null }) => (value ? dateToView(value) : "—"),
                CSVExport: ({ value }: { value?: string | null }) => (value ? dateToView(value) : ""),
            },
            {
                Header: "Статус",
                id: "shippingOrderStatus",
                accessor: getShippingOrderRegistryStatusLabel,
                filterable: true,
                filterType: "prepared",
                sortable: true,
                sortKey: "lastTitleType",
                Filter: ShippingOrderStatusFilter,
                Cell: ({ value, row: { original } }: { value: string; row: { original: ShippingOrderListModel } }) => (
                    <div className="w-100">
                        <div className="d-flex align-items-center">
                            <span
                                className={`badge text-truncate ${
                                    original.lastTitleStatus === ShippingOrderTitleStatus.Signed
                                        ? "badge-success"
                                        : "badge-secondary"
                                }`}
                                title={value}
                            >
                                {value}
                            </span>
                            <ShippingOrderDeclineBadge shippingOrder={original} />
                        </div>
                        <div className="mt-1">
                            <ShippingOrderStatusBadge status={original.status} className="mw-100 text-truncate" />
                        </div>
                    </div>
                ),
            },
            {
                Header: "Первый адрес",
                accessor: "firstAddress",
                filterable: true,
                sortable: true,
            },
            {
                Header: "Последний адрес",
                accessor: "lastAddress",
                filterable: true,
                sortable: true,
            },
            participantColumn("Отправитель", "shipper"),
            participantColumn("Перевозчик", "carrier"),
            {
                Header: "Создан заказ",
                id: "shippingOrderCreated",
                accessor: (item: ShippingOrderListModel) => Boolean(item.distributionBidId),
                filterable: true,
                filterType: "prepared",
                sortable: false,
                Filter: ShippingOrderCreatedFilter,
                Cell: ({ value }: { value: boolean }) => (value ? "Да" : "Нет"),
                CSVExport: ({ value }: { value: boolean }) => (value ? "Да" : "Нет"),
            },
            {
                Header: "Связанный заказ",
                accessor: "distributionBidId",
                filterable: true,
                filterType: "int",
                sortable: true,
                Cell: ({ value }: { value?: number | null }) =>
                    value ? (
                        <Link
                            to={paths.distributionBidShow(value)}
                            onClick={(event: MouseEvent<HTMLAnchorElement>) => event.stopPropagation()}
                        >
                            Заказ №{value}
                        </Link>
                    ) : (
                        "Не создан"
                    ),
            },
            {
                Header: "Связанная заявка",
                accessor: "bidId",
                filterable: true,
                filterType: "int",
                sortable: true,
                Cell: ({ value }: { value?: number | null }) =>
                    value ? (
                        <Link
                            to={paths.bidShow("bid", value)}
                            onClick={(event: MouseEvent<HTMLAnchorElement>) => event.stopPropagation()}
                        >
                            Заявка №{value}
                        </Link>
                    ) : (
                        "Не привязана"
                    ),
            },
        ];
    }

    renderHead = () => (
        <div className="book-list__head">
            <button
                type="button"
                className="book-list__head-close close close--sm border-0 bg-transparent"
                onClick={this.close}
                aria-label="Закрыть реестр ЭЗЗ"
            />
            <div className="book-list__head-title">Заявки (ЭЗЗ){this.renderCounter()}</div>
        </div>
    );

    renderListComponent = () => {
        const hasFilters = Object.values(this.props.params.customFilters || {}).some((filter: any) => {
            const value = filter?.value;
            if (!value) {
                return false;
            }
            if (typeof value === "object") {
                if ("status" in value) {
                    return value.status !== "all" || value.signatureRequired !== "all";
                }
                return Boolean(value.start || value.end);
            }
            return value !== "all";
        });
        return (
            <SharedReactTable
                {...this.props}
                columns={this.state.tableColumns}
                tableProps={{
                    fitTableToContainer: true,
                    noDataText: hasFilters ? "По заданным фильтрам ЭЗЗ не найдены" : "Реестр ЭЗЗ пока пуст",
                }}
            />
        );
    };

    render() {
        return (
            <div className="page-content">
                <Transition dir="full">
                    <div className="page-book__list book-list list">
                        {this.renderHead()}
                        <div className="container-fluid my-2">{this.renderActions()}</div>
                        {this.renderListComponent()}
                    </div>
                </Transition>
            </div>
        );
    }
}

export default tableContainer<ShippingOrderListModel>(ShippingOrdersList as unknown as FC, "shipping-orders");
